import { useState } from "react";
import { Heart, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { useWishlist } from "@/hooks/useWishlist";
import { canonicalProductUrl } from "@/lib/wishlist";
import { track } from "@/lib/customer-intelligence/tracker";

/**
 * Save + share controls pinned over the bottom-left of the product image
 * carousel. Purely additive: gallery images, swipe/zoom, prices, copy and
 * checkout are untouched.
 *
 * Share only ever carries the canonical public product URL.
 */

/**
 * Copies text and returns true ONLY when the copy really happened.
 * A missing clipboard API must never resolve as success.
 */
async function copyLink(url: string): Promise<boolean> {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const writeText = nav?.clipboard?.writeText;
  if (typeof writeText === "function") {
    try {
      await writeText.call(nav!.clipboard, url);
      return true;
    } catch {
      /* fall through to the legacy path */
    }
  }
  if (typeof document === "undefined") return false;
  try {
    const el = document.createElement("textarea");
    el.value = url;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok === true;
  } catch {
    return false;
  }
}

export function GalleryActions({ handle, title }: { handle: string; title: string }) {
  const { isSaved, isBusy, toggle } = useWishlist();
  const [copied, setCopied] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const saved = isSaved(handle);
  const busy = isBusy(handle);

  async function onShare() {
    const url = canonicalProductUrl(handle);
    setFallbackUrl(null);
    track("share_attempted", { handle });
    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    if (nav?.share) {
      try {
        await nav.share({ title, url });
        // The OS accepted the payload. Actual delivery is never guaranteed.
        track("share_native_handoff", { handle });
        return;
      } catch (err) {
        const name = (err as Error)?.name;
        if (name === "AbortError") {
          track("share_dismissed", { handle });
          return;
        }
        // fall through to copy
      }
    }
    const didCopy = await copyLink(url);
    if (didCopy) {
      track("share_link_copied", { handle });
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Link copied");
      return;
    }
    track("share_failed", { handle });
    setFallbackUrl(url);
    toast.error("Couldn't copy the link — copy it from the box below.");
  }

  const btn =
    "pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-background/85 shadow-sm backdrop-blur transition active:scale-95 hover:bg-background disabled:opacity-60";

  return (
    <div
      // Inline height/width beat the gallery's `.twc-selected-image > div { height:100% }` rule.
      style={{ height: "auto", width: "auto" }}
      className="pointer-events-none absolute bottom-3 left-3 z-10 flex flex-col items-start gap-2"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void toggle(handle)}
          disabled={busy}
          aria-pressed={saved}
          aria-busy={busy}
          aria-label={saved ? `Remove ${title} from saved products` : `Save ${title}`}
          className={btn}
        >
          <Heart
            className={saved ? "h-[18px] w-[18px] text-[hsl(var(--brand))]" : "h-[18px] w-[18px] text-foreground/70"}
            fill={saved ? "currentColor" : "none"}
            strokeWidth={1.8}
          />
        </button>
        <button type="button" onClick={() => void onShare()} aria-label={`Share ${title}`} className={btn}>
          {copied ? (
            <Check className="h-[18px] w-[18px] text-[hsl(var(--brand))]" strokeWidth={2} />
          ) : (
            <Share2 className="h-[18px] w-[18px] text-foreground/70" strokeWidth={1.8} />
          )}
        </button>
      </div>
      {fallbackUrl ? (
        <input
          readOnly
          value={fallbackUrl}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={`Link to ${title}`}
          className="pointer-events-auto w-[15rem] max-w-[70vw] select-all rounded-full border border-border/70 bg-background/95 px-3 py-2 text-xs text-foreground/80 shadow-sm backdrop-blur"
        />
      ) : null}
    </div>
  );
}
